import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";
import { VEHICLE_VALUATION_RESOURCE } from "./use-vehicle-valuation-search.query";

export const VEHICLE_VALUATION_CONFIRMER_SEARCH_OPERATION = "confirmers/search" as const;
export const VEHICLE_VALUATION_CONFIRMER_SEARCH_API_PATH =
  `/v1/${VEHICLE_VALUATION_RESOURCE}/${VEHICLE_VALUATION_CONFIRMER_SEARCH_OPERATION}` as const;

export type VehicleValuationConfirmerSearchParameters =
  paths[typeof VEHICLE_VALUATION_CONFIRMER_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type VehicleValuationConfirmerSearchResponse =
  paths[typeof VEHICLE_VALUATION_CONFIRMER_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useVehicleValuationConfirmerSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<VehicleValuationConfirmerSearchParameters, never>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    VehicleValuationConfirmerSearchResponse,
    Error,
    VehicleValuationConfirmerSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<VehicleValuationConfirmerSearchParameters, never>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      VEHICLE_VALUATION_RESOURCE,
      VEHICLE_VALUATION_CONFIRMER_SEARCH_OPERATION,
      { parameters: requestParameters },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<VehicleValuationConfirmerSearchResponse>(
        VEHICLE_VALUATION_CONFIRMER_SEARCH_API_PATH,
        undefined,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
